/**
 * レガシーAPI用の認証付きfetchラッパー
 *
 * RTK Query (baseQuery.ts) を使わないレガシーAPIが
 * credentials と CSRF トークンを送信するための共通クライアント。
 *
 * 401 を受けた場合は /auth/refresh を一度だけ試し、
 * 成功したら元リクエストを1回リトライする（RTK Query の baseQueryWithReauth と同じ挙動）。
 */

import { getCsrfTokenFromCookie } from "../shared/utils/csrf";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

/** 同時多発的な 401 で refresh が多重実行されないよう共有 Promise を保持 */
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        try {
            const csrfToken = getCsrfTokenFromCookie();
            const headers = new Headers();
            headers.set("X-Requested-With", "XMLHttpRequest");
            if (csrfToken) {
                headers.set("X-CSRF-Token", csrfToken);
            }
            const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: "POST",
                headers,
                credentials: "include",
            });
            return res.ok;
        } catch {
            return false;
        } finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}

function buildRequestInit(options: RequestInit): RequestInit {
    const headers = new Headers(options.headers);
    const csrfToken = getCsrfTokenFromCookie();
    if (csrfToken) {
        headers.set("X-CSRF-Token", csrfToken);
    }
    headers.set("X-Requested-With", "XMLHttpRequest");
    return { ...options, headers, credentials: "include" };
}

/**
 * 認証情報・CSRFトークン付きのfetch。
 * レガシーAPIファイルで `fetch(url, options)` の代わりに使う。
 */
export async function authFetch(
    url: string,
    options: RequestInit = {},
): Promise<Response> {
    let response = await fetch(url, buildRequestInit(options));

    if (response.status === 401 && !url.includes("/auth/refresh")) {
        const refreshed = await tryRefresh();
        if (refreshed) {
            // Cookie が更新されたので元のリクエストを1回だけリトライ
            response = await fetch(url, buildRequestInit(options));
        }
    }

    return response;
}

export { API_BASE_URL };
